import { useRef, useState } from 'react'
import {
  ArrowLeft,
  ArrowUpRight,
  Globe,
  LogOut,
  Plus,
  Search,
  SlidersHorizontal,
} from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useFeatureFlagManagement } from '@/hooks/use-feature-flags'
import type {
  FeatureFlagDTO,
  FeatureFlagKey,
} from '@/schemas/feature-flag.schema'
import { Button } from '@/components/ui/button'
import { clearAuth } from '@/lib/auth'
import { CreateFeatureFlagForm } from '../components/create-feature-flag-form'
import { DeleteFeatureFlagButton } from '../components/delete-feature-flag-button'
import { FeatureFlagEditor } from '../components/feature-flag-editor'
import { audienceLabel } from '../lib/feature-flag-audience-labels'
import './feature-flags.css'

const isOn = (flag: FeatureFlagDTO) => flag.audience !== 'OFF'

// The reload drops the query cache and lands on the sign-in page.
function signOut() {
  clearAuth()
  window.location.reload()
}

function audience(flag: FeatureFlagDTO) {
  const label = audienceLabel(flag.audience)
  if (flag.audience !== 'WHITELIST') {
    return label
  }
  const count = flag.whitelist.length
  return `${label} · ${count} ${count === 1 ? 'company' : 'companies'}`
}

export function FeatureFlagsPage() {
  useDocumentTitle('Feature flags')
  const query = useFeatureFlagManagement()
  const scrollContainer = useRef<HTMLDivElement>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'enabled' | 'disabled'>('all')
  const [creating, setCreating] = useState(false)
  // The open flag is in the URL (/ff/KEY): a link opens it, and reload and
  // back keep it. One route for both, so the editors and their unsaved
  // edits stay mounted while you move between flags.
  const selected = useParams().key ?? null
  const navigate = useNavigate()
  function showFlag(key: FeatureFlagKey | null) {
    setCreating(false)
    navigate(key ? `/ff/${key}` : '/ff')
    scrollContainer.current?.scrollTo({ top: 0 })
  }
  const flags = query.data?.items ?? []
  const visibleFlags = flags.filter(
    (flag) =>
      `${flag.name} ${flag.key} ${flag.description}`
        .toLowerCase()
        .includes(search.toLowerCase()) &&
      (filter === 'all' || isOn(flag) === (filter === 'enabled')),
  )
  return (
    <div className="ff-console" ref={scrollContainer}>
      <main className="ff-main">
        <div hidden={flags.some((flag) => flag.key === selected)}>
          <header className="ff-page-header">
            <div className="ff-header-actions">
              <Button
                className="ff-create-button"
                size="sm"
                onClick={() => {
                  setCreating(true)
                  scrollContainer.current?.scrollTo({ top: 0 })
                }}
              >
                <Plus size={15} />
                Create flag
              </Button>
            </div>
            <div className="ff-title-row">
              <div>
                <p className="ff-eyebrow">RELEASE CONTROLS</p>
                <h1>Feature flags</h1>
              </div>
            </div>
            <p className="ff-intro">
              One place to control who gets access. Pick a flag to manage its
              audience.
            </p>
          </header>
          <div className="ff-toolbar">
            <div className="ff-filters" aria-label="Filter by status">
              {(['all', 'enabled', 'disabled'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={filter === value}
                  onClick={() => setFilter(value)}
                >
                  {value === 'all'
                    ? 'All flags'
                    : value === 'enabled'
                      ? 'On'
                      : 'Off'}
                  <span>
                    {
                      flags.filter(
                        (flag) =>
                          value === 'all' ||
                          isOn(flag) === (value === 'enabled'),
                      ).length
                    }
                  </span>
                </button>
              ))}
            </div>
            <label className="ff-search">
              <Search size={16} />
              <input
                aria-label="Search feature flags"
                placeholder="Search flags…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
          </div>
          <section className="ff-table-panel" aria-label="Feature flags">
            {query.isPending ? (
              <div className="ff-empty" role="status">
                Loading feature flags…
              </div>
            ) : query.isError ? (
              <div className="ff-empty" role="alert">
                Could not load feature flags.{' '}
                <button type="button" onClick={() => query.refetch()}>
                  Try again
                </button>
              </div>
            ) : (
              <div className="ff-table-scroll">
                <table className="ff-table">
                  <thead>
                    <tr>
                      <th scope="col">Feature</th>
                      <th scope="col">Key</th>
                      <th scope="col">Status</th>
                      <th scope="col">Audience</th>
                      <th scope="col">
                        <span className="sr-only">Details</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFlags.map((flag) => (
                      <tr key={flag.key}>
                        <td>
                          <button
                            type="button"
                            className="ff-flag-link"
                            aria-label={`Edit ${flag.name}`}
                            onClick={() => showFlag(flag.key)}
                          >
                            <span className="ff-row-title">
                              <span>{flag.name}</span>
                            </span>
                            <span className="ff-row-description">
                              {flag.description || 'No description'}
                            </span>
                          </button>
                        </td>
                        <td>
                          <code className="ff-table-key">{flag.key}</code>
                        </td>
                        <td>
                          <span
                            className={`ff-status ${isOn(flag) ? 'ff-status-on' : ''}`}
                          >
                            <span />
                            {isOn(flag) ? 'On' : 'Off'}
                          </span>
                        </td>
                        <td>
                          <div
                            className={`ff-audiences ${!isOn(flag) ? 'ff-muted-audiences' : ''}`}
                          >
                            {flag.audience === 'EVERYONE' ? (
                              <span className="ff-audience-everyone">
                                <Globe size={12} />
                                {audience(flag)}
                              </span>
                            ) : (
                              <span>{audience(flag)}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="ff-detail-link" aria-hidden="true">
                            <ArrowUpRight size={17} />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {visibleFlags.length === 0 ? (
                  <div className="ff-empty">
                    No flags match your filters.{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setSearch('')
                        setFilter('all')
                      }}
                    >
                      Clear filters
                    </button>
                  </div>
                ) : null}
              </div>
            )}
            <footer className="ff-table-footer">
              <span>
                {visibleFlags.length} of {flags.length} flags
              </span>
              <span>
                <SlidersHorizontal size={13} />
                One audience tier per flag
              </span>
            </footer>
          </section>
          <p className="ff-footnote">
            Changes apply at once in the API. Users get them within about an
            hour, or on reload.
          </p>
        </div>
        {creating ? (
          <CreateFeatureFlagForm
            onCreated={(key) => {
              setSearch('')
              setFilter('all')
              showFlag(key)
            }}
            onCancel={() => setCreating(false)}
          />
        ) : null}
        {flags.map((flag) => (
          <div key={flag.key} hidden={selected !== flag.key}>
            <nav className="ff-detail-nav">
              <button
                type="button"
                className="ff-back"
                onClick={() => showFlag(null)}
              >
                <ArrowLeft size={15} />
                All feature flags
              </button>
              <DeleteFeatureFlagButton
                flag={flag}
                onDeleted={() => showFlag(null)}
              />
            </nav>
            {/* Keyed by the stored rule: when it changes, by a save here or
                by a colleague, the editor starts again from what is stored. */}
            <FeatureFlagEditor
              key={`${flag.audience}:${flag.whitelist.join(',')}`}
              flag={flag}
            />
          </div>
        ))}
      </main>
      {/* Pinned, so it is there on the list and in a flag's editor alike. */}
      <Button
        variant="outline"
        size="sm"
        className="fixed bottom-4 left-4"
        onClick={signOut}
      >
        <LogOut size={15} />
        Sign out
      </Button>
    </div>
  )
}
